// @flow

import * as React from "react";
import ally from "ally.js";

import AnimateHeight from "./AnimateHeight";
import createUniqueID from "../modules/util/createUniqueID";

const { useState, useEffect } = React;

type PropsType = {
    title: string | React.Node,
    contentString?: string,
    children?: React.Node,
    tag?: string,
};

const Accordion = (props: PropsType): React.Node => {
    const labelId: string = createUniqueID();
    const contentId: string = createUniqueID();
    const [expanded, setExpanded] = useState(false);
    const TitleTag = props.tag || "h4";

    const toggleExpansion = () => {
        setExpanded((a: boolean): boolean => !a);
    };

    // Automatically sets a display of none to all focusable elements
    // whenever the accordion is closed. Resets these params after opening again.
    useEffect((): (() => void) => {
        let elements;
        if (!expanded) {
            elements = ally.query.focusable({
                context: document.getElementById(contentId),
                includeContext: true,
                strategy: "quick",
            });
            elements.forEach((el: HTMLElement) => {
                el.setAttribute("data-natural-display", el.style.display);
                el.style.display = "none";
            });
        }
        return () => {
            if (elements) {
                // Enable all the elements
                elements.forEach((el: HTMLElement) => {
                    const naturalDisplay = el.getAttribute(
                        "data-natural-display"
                    );
                    if (naturalDisplay) {
                        el.style.display = naturalDisplay;
                    } else {
                        el.style.display = "";
                    }
                });
            }
        };
    }, [expanded]);

    return (
        <div
            // Give it a wrapping .accordion class if being rendered from react rather than from twig file
            className={
                !props.contentString && props.children ? "accordion" : ""
            }
        >
            <button
                type="button"
                className="accordion__button"
                onClick={toggleExpansion}
                aria-expanded={expanded}
                aria-controls={contentId}
                aria-labelledby={labelId}
            >
                <TitleTag className="accordion__title" id={labelId}>
                    {props.title}
                </TitleTag>
                <i
                    aria-hidden
                    className={`icon icon-arrow-${
                        expanded ? "up" : "down"
                    } accordion__arrow`}
                ></i>
            </button>
            <AnimateHeight
                duration={500}
                hideOverflow={true}
                height={!expanded ? 0 : null}
                className="accordion__content"
            >
                {props.contentString ? (
                    <div
                        role="region"
                        id={contentId}
                        aria-labelledby={labelId}
                        aria-hidden={!expanded}
                        aria-expanded={expanded}
                        className="accordion__content__inner rte"
                        dangerouslySetInnerHTML={{
                            __html: props.contentString,
                        }}
                    ></div>
                ) : props.children ? (
                    <div
                        role="region"
                        id={contentId}
                        aria-labelledby={labelId}
                        aria-hidden={!expanded}
                        aria-expanded={expanded}
                        className="accordion__content__inner rte"
                    >
                        {props.children}
                    </div>
                ) : null}
            </AnimateHeight>
        </div>
    );
};

export default Accordion;
